import axios, { AxiosError, AxiosInstance } from 'axios';
import { toast } from 'react-toastify';
import { tokenStorage } from './tokenStorage';

let unauthorizedHandler: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: () => void) => {
  unauthorizedHandler = handler;
};

const createClient = (): AxiosInstance => {
  const baseURL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5003';
  const client = axios.create({
    baseURL,
    timeout: 30000,
  });

  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    console.debug('[apiClient] baseURL', baseURL);
  }

  client.interceptors.request.use((config) => {
    const tokens = tokenStorage.read();
    if (tokens?.accessToken) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;

      if (status === 401) {
        tokenStorage.clear();
        if (unauthorizedHandler) {
          unauthorizedHandler();
        }
      } else if (status === 403) {
        toast.error('Access denied – please contact an administrator.');
      } else if (status && status >= 500) {
        toast.error('Server error – please try again in a moment.');
      } else if (!status) {
        toast.error('Network error – please check your connection.');
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = createClient();

export interface ApiPaginationParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

export const buildPaginationParams = ({
  page = 1,
  pageSize = 20,
  search,
}: ApiPaginationParams) => ({
  page,
  pageSize,
  ...(search ? { search } : {}),
});
